/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { Card, Col, Row } from "reactstrap";
import { CardBody, CardHeader, Spinner } from "react-bootstrap";
import ButtonComponent from "../../components/ButtonComponent";
import { useNavigate } from "react-router-dom";
import "./index.css";
import RestaurantDetailsTable from "./RestaurantDetailsTable";
import {
  openInitialSlectReastaurantModla,
  setSelectedRestaurantDetails,
} from "../../redux/restaurantsSlice";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import BreadCrumbs from "../../components/BreadCrumbs";
import {
  useLazyGetRestaurantDetailsByIdQuery,
} from "../../redux/services/restaurantServices";
import { clearSelectedMenuType } from "../../redux/customizeMenuSlice";
import { clearSelectedOrderDate, clearSelectedOrderType } from "../../redux/orderSlice";
import { clearRowsPerPage } from "../../redux/menusSlice";
import ManImage from "../../assets/icons/man.PNG";
import ChangePasswordModal from "../../components/ChangePasswordModal"; // Add this import
import { auth } from "../../firebase"; // Add this import
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail
} from "firebase/auth"; // Add these imports
import { toast } from "react-toastify"; // Add this import

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.id
  );

  const [trigger, { data: selectedRestaurantDetails, isLoading, error }] =
    useLazyGetRestaurantDetailsByIdQuery();

  const [userEmail, setUserEmail] = useState("No Email");
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false); // Add this state

  useEffect(() => {
    const storedEmail = localStorage.getItem("email");
    if (storedEmail) {
      setUserEmail(storedEmail);
    }
    if (selectedRestaurantId) {
      trigger(selectedRestaurantId);
    }
  }, [selectedRestaurantId, trigger]);

  // Update Redux store when restaurant details are fetched
  useEffect(() => {
    if (selectedRestaurantDetails?.restaurant) {
      dispatch(setSelectedRestaurantDetails(selectedRestaurantDetails.restaurant));
    }
  }, [selectedRestaurantDetails, dispatch]);

  // Password change handler
  const handleChangePassword = async (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error("New passwords do not match");
      return false;
    }

    if (data.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return false;
    }

    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        toast.error("User not authenticated");
        return false;
      }

      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(user.email, data.currentPassword);
      await reauthenticateWithCredential(user, credential);

      await updatePassword(user, data.newPassword);
      toast.success("Password changed successfully!");

      setShowChangePasswordModal(false);
      return true;
    } catch (error: any) {
      console.error("Password change error:", error);

      let errorMessage = "Failed to change password. Please try again.";

      switch (error.code) {
        case 'auth/wrong-password':
          errorMessage = "Current password is incorrect.";
          break;
        case 'auth/weak-password':
          errorMessage = "New password is too weak. Please choose a stronger password.";
          break;
        case 'auth/requires-recent-login':
          errorMessage = "For security reasons, please log out and log back in before changing your password.";
          break;
        case 'auth/too-many-requests':
          errorMessage = "Too many attempts. Please try again later.";
          break;
        case 'auth/network-request-failed':
          errorMessage = "Network error. Please check your connection and try again.";
          break;
        default:
          errorMessage = error.message || "An error occurred while changing your password.";
      }

      toast.error(errorMessage);
      return false;
    }
  };

  // Password reset via email handler
  const handlePasswordResetEmail = async () => {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        toast.error("User not authenticated");
        return;
      }

      const result = await Swal.fire({
        title: 'Reset Password via Email',
        text: `Send a password reset link to ${user.email}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#FF1001',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Send Reset Link',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        await sendPasswordResetEmail(auth, user.email, {
          url: `${window.location.origin}/sign-in`,
          handleCodeInApp: false,
        });

        Swal.fire({
          title: 'Password Reset Email Sent!',
          html: `
            <p>We've sent a password reset link to <strong>${user.email}</strong></p>
            <p>Please check your email and follow the instructions to reset your password.</p>
            <small class="text-muted">You'll need to log in again after resetting your password.</small>
          `,
          icon: 'success',
          confirmButtonColor: '#FF1001',
        });
      }
    } catch (error: any) {
      console.error("Password reset email error:", error);
      toast.error("Failed to send password reset email. Please try again.");
    }
  };

  const handleLogOut = () => {
    Swal.fire({
      title: "Are you sure you want to log out?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, log out",
    }).then((result: any) => {
      if (result.isConfirmed) {
        localStorage.clear();
        dispatch(openInitialSlectReastaurantModla(false));
        dispatch(setSelectedRestaurantDetails(null));
        dispatch(clearSelectedMenuType());
        dispatch(clearSelectedOrderType());
        dispatch(clearSelectedOrderDate());
        dispatch(clearRowsPerPage());

        // Dispatch custom event to notify navigation of auth state change
        window.dispatchEvent(new CustomEvent('authStateChanged'));

        navigate("/sign-in");
      }
    });
  };

  const handleLocationAdd = () => {
    navigate("/profile/add-restaurant");
  };

  return (
    <div className="profile-container">
      <BreadCrumbs hasSubMenus={false} />
      <br />
      <Card className="profile-card">
        <CardHeader className="profile-card-header">
          <h4>Profile</h4>
          <ButtonComponent titel={"Log Out"} onClick={handleLogOut} />
        </CardHeader>
        <CardBody className="profile-card-body">
          <img className="profile-avatar" src={ManImage} alt="Profile Avatar" />
          <h5>{userEmail}</h5>



          {/* Add password management section */}
          <div className="password-management-section mt-3">
            <div className="d-flex justify-content-left gap-2 flex-wrap">
              <ButtonComponent
                titel="Change Password"
                onClick={() => setShowChangePasswordModal(true)}

              />
              <ButtonComponent
                titel="Reset via Email"
                onClick={handlePasswordResetEmail}

              />
            </div>
          </div>

          <hr />

          <div className="restaurant-details">
            <h4 className="restaurant-details-heading">
              Selected Restaurant Details
            </h4>

            <div className="restaurant-details-table-whole-component">
              {isLoading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "200px" }}
                >
                  <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <span className="ms-2">Please Wait...</span>
                </div>
              ) : (
                <Row>
                  <Col lg="4">
                    <img
                      src={selectedRestaurantDetails?.restaurant?.imageUrl}
                      alt="Restaurant"
                      className="restaurant-image"
                    />
                  </Col>
                  <Col lg="8">
                    <table className="table restaurant-details-table">
                      <tbody>
                        <tr>
                          <td>Restaurant Name</td>
                          <th>
                            :{" "}
                            {selectedRestaurantDetails?.restaurant?.displayName}
                          </th>
                        </tr>
                        <tr>
                          <td>Address</td>
                          <th>
                            : {selectedRestaurantDetails?.restaurant?.address}
                          </th>
                        </tr>
                        <tr>
                          <td>Restaurant Open Time</td>
                          <th>
                            :{" "}
                            {selectedRestaurantDetails?.restaurant
                              ?.openingTime === "" ||
                              selectedRestaurantDetails?.restaurant
                                ?.openingTime === null
                              ? "No Open Time"
                              : selectedRestaurantDetails?.restaurant
                                ?.closingTime === "" ||
                                selectedRestaurantDetails?.restaurant
                                  ?.closingTime === null
                                ? "No Close Time"
                                : (selectedRestaurantDetails?.restaurant
                                  ?.openingTime === "" ||
                                  selectedRestaurantDetails?.restaurant
                                    ?.openingTime === null) &&
                                  (selectedRestaurantDetails?.restaurant
                                    ?.closingTime === "" ||
                                    selectedRestaurantDetails?.restaurant
                                      ?.closingTime === null)
                                  ? "No Open Time & Close Time"
                                  : `${selectedRestaurantDetails?.restaurant?.openingTime} to ${selectedRestaurantDetails?.restaurant?.closingTime}`}
                          </th>
                        </tr>
                        <tr>
                          <td>Categories</td>
                          <th>
                            :{" "}
                            {selectedRestaurantDetails?.restaurant?.categoryTags?.map(
                              (categoryTags: string, index: number) => (
                                <span key={index}>
                                  {categoryTags.charAt(0).toUpperCase() +
                                    categoryTags.slice(1).toLowerCase()}
                                  {index <
                                    selectedRestaurantDetails?.restaurant
                                      ?.categoryTags.length -
                                    1
                                    ? ", "
                                    : ""}
                                </span>
                              )
                            )}
                          </th>
                        </tr>
                      </tbody>
                    </table>
                  </Col>
                </Row>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
      <div className="add-restaurant-btn">
        <ButtonComponent titel="Add Restaurant" onClick={handleLocationAdd} />
      </div>
      <Card className="profile-card">
        <CardHeader className="profile-card-header">
          <h4>Your Existing Restaurant List</h4>
        </CardHeader>
        <CardBody className="profile-card-body">
          <RestaurantDetailsTable />
        </CardBody>
      </Card>

      {/* Change Password Modal */}
      <ChangePasswordModal
        show={showChangePasswordModal}
        onHide={() => setShowChangePasswordModal(false)}
        onSubmit={handleChangePassword}
      />
    </div>
    //Comment
  );
};

export default Profile;
